import { useEffect, useState, useRef } from "react";
import {
  Form,
  Row,
  Col,
  Card,
  Typography,
  Divider,
  Spin,
  Checkbox,
  App,
  Modal,
  Button,
  message as antdMessage,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import CustomInputNumber from "../../../components/CustomInputNumber/InputNumber";
import CustomInput from "../../../components/CustomInput/Input";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import FormFooter from "../../../components/CustomForms/FormFooter";
import {
  BTN_TXT,
  BUS_ENT_DOC_CONFIG,
  ERROR_MSGS,
  CREDIT_LIMIT_MSGS,
  CREDIT_LIMIT_SUPER_ADMIN_THRESHOLD,
  isRootOrSuperAdmin,
} from "../../../constants/constants";
import {
  formatFee,
  discountRateRules,
  maxFundRequiredPctRules,
  CommentsRules,
  convertToPercentage,
  formatDiscountRatePercent,
  formatMaxFundRequiredPercent,
  percentToDiscountRate,
  percentToMaxFundRequiredPct,
} from "../../../helpers/helpers";
import { getDealCategoryDisplayName } from "../../../helpers/dealCategoryProducts";
import {
  getPlatformFeeData,
  approveBusEntbyId,
  approveBusEnt,
  getBusEnt,
  addCheckBox,
  deleteCheckBox,
  approveCreditLimitRequest,
  getCreditLimitRequests,
} from "../../../redux/slices/creditLimitSlice";
import { count } from "../../../redux/slices/dealsSlice";
import { count as allCount } from "../../../redux/slices/AuthSlice";
import { MinusCircleOutlined } from "@ant-design/icons";
import BusEntDocUploadCard from "../../../components/DocUploadCard/BusEntDocUploadCard";

const { Text } = Typography;
const protectedCheckboxIds = ["CHB1000001", "CHB1000002", "CHB1000003"];

const areCheckboxDetailsEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every(
    (item, idx) =>
      item.checkboxId === b[idx]?.checkboxId &&
      item.checkboxName === b[idx]?.checkboxName &&
      Boolean(item.box) === Boolean(b[idx]?.box)
  );
};

const ApproveEntBus = ({ data, onClose, status }) => {
  const app = App.useApp();
  const message = app?.message || antdMessage;
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const {
    platformFeeData,
    approveSubmitLoading,
    requestApproveLoading,
    error,
    busEntByIdData,
    busEntByIdLoading,
    addSubmitLoading,
    deleteLoading,
  } = useSelector((state) => state.creditLimit);
  const { user } = useSelector((state) => state.auth || {});
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [superAdminRestrictionOpen, setSuperAdminRestrictionOpen] = useState(false);
  const [checkboxList, setCheckboxList] = useState([]);
  const [customCheckbox, setCustomCheckbox] = useState("");
  const [file, setFile] = useState({ fileUrl: null, fileName: null });
  const [checkboxError, setCheckboxError] = useState("");
  const lastDataKeyRef = useRef(null);
  const prevCheckboxDetailsRef = useRef(null);

  const validateCheckboxName = (value) => {
    const trimmed = value?.trim();

    if (!trimmed) {
      return "Please enter checkbox name";
    }

    if (trimmed.length > 200) {
      return "Checkbox name cannot exceed 200 characters";
    }

    // Must contain at least one alphabet
    if (!/[A-Za-z]/.test(trimmed)) {
      return "Checkbox name must contain at least one alphabet";
    }

    return "";
  };

  // Load Platform Fee Data
  useEffect(() => {
    if (!platformFeeData || platformFeeData.length === 0) {
      dispatch(getPlatformFeeData());
    }
  }, [dispatch, platformFeeData]);

  // Synchronize default platform fee once platformFeeData arrives
  useEffect(() => {
    if (platformFeeData?.length > 0) {
      const currentPlatformFee = form.getFieldValue("platformFee");
      if (!currentPlatformFee) {
        const defaultPlatformId =
          data?.platformFeeId ||
          busEntByIdData?.platformFeeId ||
          platformFeeData[0]?.platformFeeId;
        if (defaultPlatformId) {
          form.setFieldsValue({ platformFee: defaultPlatformId });
          const platform =
            platformFeeData.find(
              (item) => String(item.platformFeeId) === String(defaultPlatformId)
            ) || null;
          setSelectedPlatform(platform);
        }
      } else if (!selectedPlatform) {
        const platform =
          platformFeeData.find(
            (item) => String(item.platformFeeId) === String(currentPlatformFee)
          ) || null;
        setSelectedPlatform(platform);
      }
    }
  }, [platformFeeData, data?.platformFeeId, busEntByIdData?.platformFeeId, form, selectedPlatform]);

  const handlePlatformChange = (value) => {
    form.setFieldsValue({ platformFee: value });
    const platform =
      platformFeeData?.find(
        (item) => String(item.platformFeeId) === String(value)
      ) || null;
    setSelectedPlatform(platform);
  };

  // Load BusEntById / Checkbox data from API
  useEffect(() => {
    if (data?.busEntId) {
      dispatch(approveBusEntbyId({ busEntId: data.busEntId, requestId: data.requestId }));
    } else if (data?.requestId) {
      dispatch(approveBusEntbyId({ requestId: data.requestId }));
    }
  }, [dispatch, data?.busEntId, data?.requestId]);

  const handleAddCheckbox = async () => {
    const checkboxName = customCheckbox?.trim();

    const validationError = validateCheckboxName(checkboxName);

    if (validationError) {
      message.error(validationError);
      return;
    }

    setCheckboxError("");

    const targetBusEntId = data?.busEntId || busEntByIdData?.busEntId;

    if (!targetBusEntId) {
      const newId = `CHB_CUSTOM_${Date.now()}`;
      const newCheckbox = {
        checkboxId: newId,
        checkboxName,
        value: false,
        isCustom: true,
      };

      setCheckboxList((prev) => [...prev, newCheckbox]);
      form.setFieldsValue({
        [newId]: false,
      });
      setCustomCheckbox("");
      setCheckboxError("");
      return;
    }

    const payload = {
      checkboxName,
      busEntId: targetBusEntId,
    };

    try {
      const result = await dispatch(addCheckBox(payload));

      if (result?.payload?.statusCode === 201) {
        dispatch(approveBusEntbyId({ busEntId: targetBusEntId, requestId: data?.requestId }));

        const apiData = result.payload.response;

        const newCheckbox = {
          checkboxId: apiData.checkboxId,
          checkboxName: apiData.checkboxName,
          value: false,
          isCustom: true,
        };

        setCheckboxList((prev) => [...prev, newCheckbox]);

        form.setFieldsValue({
          [apiData.checkboxId]: false,
        });

        setCustomCheckbox("");
        setCheckboxError("");
      }
    } catch (err) {
      message.error("Error adding checkbox:", err);
    }
  };

  const handleRemoveCheckbox = async (checkboxId) => {
    const targetBusEntId = data?.busEntId || busEntByIdData?.busEntId;

    if (!targetBusEntId) {
      setCheckboxList((prev) =>
        prev.filter((item) => item.checkboxId !== checkboxId)
      );
      form.resetFields([checkboxId]);
      return;
    }

    try {
      const result = await dispatch(deleteCheckBox(checkboxId));

      if (result?.payload?.statusCode === 200) {
        setCheckboxList((prev) =>
          prev.filter((item) => item.checkboxId !== checkboxId),
        );

        form.resetFields([checkboxId]);
        dispatch(approveBusEntbyId({ busEntId: targetBusEntId, requestId: data?.requestId }));
      }
    } catch (err) {
      message.error("Error deleting checkbox:", err);
    }
  };

  // Set form values when data or busEntByIdData changes
  useEffect(() => {
    if (!data) return;

    const dataKey = `${data?.requestId || ""}_${data?.busEntId || ""}`;
    const isNewRecord = lastDataKeyRef.current !== dataKey;

    if (isNewRecord) {
      lastDataKeyRef.current = dataKey;
      prevCheckboxDetailsRef.current = null;
      form.resetFields();

      if (data?.requestId) {
        form.setFieldsValue({
          dealCategory: getDealCategoryDisplayName(
            data.dealCategoryId,
            data.dealCategoryName,
          ),
          busName: data.orgName || data.orgId,
          entName: data.enterpriseName || data.entName,
          prevCreditLimit: data.previousCredit ?? 0,
          requestedCreditLimit: data.requestedCredit ? Number(data.requestedCredit) : undefined,
          approvedCredit: undefined,
          discountRate: data.requestedDiscountRate
            ? formatDiscountRatePercent(data.requestedDiscountRate)
            : undefined,
          maxFundRequiredPct: formatMaxFundRequiredPercent(0.8),
          platformFee: data.platformFeeId || busEntByIdData?.platformFeeId || platformFeeData?.[0]?.platformFeeId,
          comments: undefined,
        });

        setSelectedPlatform(
          platformFeeData?.find(
            (item) =>
              String(item.platformFeeId) ===
              String(data.platformFeeId || busEntByIdData?.platformFeeId || platformFeeData?.[0]?.platformFeeId)
          ) || null
        );
      } else if (busEntByIdData) {
        form.setFieldsValue({
          dealCategory: getDealCategoryDisplayName(
            data.dealCategoryId,
            data.dealCategoryName,
          ),
          busName: data.orgName,
          entName: data.entName,
          prevCreditLimit: data.previousCredit,
          requestedCreditLimit: data.requestedCredit,
          discountRate: formatDiscountRatePercent(data.annualDiscountRate),
          maxFundRequiredPct: formatMaxFundRequiredPercent(data.maxFundRequiredPct ?? 0.8),
          platformFee: data.platformFeeId,
        });

        setSelectedPlatform(
          platformFeeData?.find(
            (item) =>
              String(item.platformFeeId) === String(busEntByIdData.platformFeeId),
          ) || null,
        );
      }
    } else if (!data?.requestId && busEntByIdData && !form.getFieldValue("busName")) {
      form.setFieldsValue({
        dealCategory: getDealCategoryDisplayName(
          data.dealCategoryId,
          data.dealCategoryName,
        ),
        busName: data.orgName,
        entName: data.entName,
        prevCreditLimit: data.previousCredit,
        requestedCreditLimit: data.requestedCredit,
        discountRate: formatDiscountRatePercent(data.annualDiscountRate),
        maxFundRequiredPct: formatMaxFundRequiredPercent(data.maxFundRequiredPct ?? 0.8),
        platformFee: data.platformFeeId,
      });

      setSelectedPlatform(
        platformFeeData?.find(
          (item) =>
            String(item.platformFeeId) === String(busEntByIdData.platformFeeId),
        ) || null,
      );
    }
  }, [data, busEntByIdData, platformFeeData, form]);

  // Dynamically populate checkbox options and selected states from API response
  useEffect(() => {
    if (!busEntByIdData?.checkboxDetails) return;

    if (
      areCheckboxDetailsEqual(
        prevCheckboxDetailsRef.current,
        busEntByIdData.checkboxDetails
      )
    ) {
      return;
    }
    prevCheckboxDetailsRef.current = busEntByIdData.checkboxDetails;

    const checkboxes = busEntByIdData.checkboxDetails.map((item) => ({
      ...item,
      value:
        form?.getFieldValue && form.getFieldValue(item.checkboxId) !== undefined
          ? form.getFieldValue(item.checkboxId)
          : Boolean(item.box),
    }));

    setCheckboxList(checkboxes);

    const checkboxFieldsToSet = {};
    checkboxes.forEach((cb) => {
      if (!form?.getFieldValue || form.getFieldValue(cb.checkboxId) === undefined) {
        checkboxFieldsToSet[cb.checkboxId] = Boolean(cb.box);
      }
    });

    if (Object.keys(checkboxFieldsToSet).length > 0) {
      form.setFieldsValue(checkboxFieldsToSet);
    }
  }, [busEntByIdData?.checkboxDetails, form]);

  const handleSubmit = async (values) => {
    const approvedAmount = Number(values.approvedCredit);
    if (
      approvedAmount > CREDIT_LIMIT_SUPER_ADMIN_THRESHOLD &&
      !isRootOrSuperAdmin(user)
    ) {
      setSuperAdminRestrictionOpen(true);
      return;
    }

    // Collect checked checkbox ids and custom checkboxes
    const standardCheckboxIds = [];
    const customCheckboxes = [];

    checkboxList.forEach((cb) => {
      const isChecked = Boolean(values[cb.checkboxId]);
      const isCustom =
        cb.isCustom ||
        (typeof cb.checkboxId === "string" && cb.checkboxId.startsWith("CHB_CUSTOM_"));

      if (isCustom) {
        customCheckboxes.push({
          tempId: cb.checkboxId,
          checkboxName: cb.checkboxName,
          checked: isChecked,
        });
      } else if (isChecked) {
        standardCheckboxIds.push(cb.checkboxId);
      }
    });

    const businessName = data?.orgName || data?.busName || "Business";
    const enterpriseName = data?.enterpriseName || data?.entName || "Enterprise";

    if (data?.requestId) {
      const payload = {
        entId: data.resolvedEntId || data.entId,
        approvedCredit: values.approvedCredit,
        platformFeeId: values.platformFee,
        discountRate: percentToDiscountRate(values.discountRate),
        maxFundRequiredPct: percentToMaxFundRequiredPct(values.maxFundRequiredPct),
        comments: values.comments,
        checkbox: standardCheckboxIds,
        sanction_documents: file?.fileUrl || null,
        ...(customCheckboxes.length > 0 ? { customCheckboxes } : {}),
      };

      const result = await dispatch(
        approveCreditLimitRequest({
          requestId: data.requestId,
          payload,
        })
      );

      if (
        result?.meta?.requestStatus === "fulfilled" &&
        result?.payload?.statusCode === 201
      ) {
        message.success(
          CREDIT_LIMIT_MSGS?.APPROVED
            ? CREDIT_LIMIT_MSGS.APPROVED.replace("{business}", businessName).replace(
                "{enterprise}",
                enterpriseName,
              )
            : `${businessName} approved for ${enterpriseName}`
        );
        dispatch(getCreditLimitRequests());
        if (status) {
          dispatch(getBusEnt(status));
        }
        dispatch(count("CREDITLIMIT"));
        dispatch(allCount("ALL"));
        onClose();
        return;
      }

      const errorMessage =
        (result?.payload &&
          (result.payload.message ||
            result.payload.error ||
            String(result.payload))) ||
        (result?.error && (result.error.message || String(result.error))) ||
        (CREDIT_LIMIT_MSGS?.FAILED_APPROVE
          ? CREDIT_LIMIT_MSGS.FAILED_APPROVE.replace(
              "{business}",
              businessName,
            ).replace("{enterprise}", enterpriseName)
          : `Failed to approve credit limit for ${businessName}`);

      message.error(errorMessage);
      return;
    }

    const payload = {
      approvedCredit: values.approvedCredit,
      comments: values.comments,
      busEntId: data.busEntId,
      platformFeeId: values.platformFee,
      discountRate: percentToDiscountRate(values.discountRate),
      maxFundRequiredPct: percentToMaxFundRequiredPct(values.maxFundRequiredPct),
      checkbox: standardCheckboxIds,
      sanction_documents: file.fileUrl,
    };
    const result = await dispatch(approveBusEnt(payload));

    if (
      result?.meta?.requestStatus === "fulfilled" &&
      result?.payload?.statusCode === 201
    ) {
      message.success(
        CREDIT_LIMIT_MSGS?.APPROVED
          ? CREDIT_LIMIT_MSGS.APPROVED.replace("{business}", businessName).replace(
              "{enterprise}",
              enterpriseName,
            )
          : `${businessName} approved for ${enterpriseName}`
      );
      dispatch(getBusEnt(status));
      dispatch(count("CREDITLIMIT"));
      dispatch(allCount("ALL"));
      onClose();
      return;
    }

    const errorMessage =
      (result?.payload &&
        (result.payload.message ||
          result.payload.error ||
          String(result.payload))) ||
      (result?.error && (result.error.message || String(result.error))) ||
      (CREDIT_LIMIT_MSGS?.FAILED_APPROVE
        ? CREDIT_LIMIT_MSGS.FAILED_APPROVE.replace(
            "{business}",
            businessName,
          ).replace("{enterprise}", enterpriseName)
        : `Failed to approve credit limit for ${businessName}`);

    message.error(errorMessage);
  };

  return (
    <>
    <Form layout="vertical" form={form} onFinish={handleSubmit}>
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <CustomInput
            label="Deal Category"
            name="dealCategory"
            disabled
            isRequired={true}
          />
        </Col>
        <Col xs={24} md={12}>
          <CustomInput
            label="Enterprise Name"
            name="entName"
            disabled
            isRequired={true}
          />
        </Col>
        <Col xs={24} md={12}>
          <CustomInput
            label="Business Name"
            name="busName"
            disabled
            isRequired={true}
          />
        </Col>
        <Col xs={24} md={12}>
          <CustomInputNumber
            label="Previous Credit Limit"
            name="prevCreditLimit"
            disabled
            isRequired={true}
          />
        </Col>
        <Col xs={24} md={12}>
          <CustomInputNumber
            label="Requested Credit Limit"
            name="requestedCreditLimit"
            disabled
            isRequired={true}
          />
        </Col>
        <Col xs={24} md={12}>
          <CustomInputNumber
            label="Approved Credit Limit"
            name="approvedCredit"
            placeholder="Enter approved credit limit"
            controls={false}
            isRequired={true}
            dependencies={["requestedCreditLimit"]}
            rules={[
              { required: true, message: ERROR_MSGS.APPRO_CRED_ERR_MSG },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const requested = getFieldValue("requestedCreditLimit");
                  if (value > requested) {
                    return Promise.reject(
                      new Error(
                        "Approved Credit cannot be greater than Requested Credit Limit",
                      ),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          />
        </Col>
        <Col xs={24} md={12}>
          <CustomInput
            label="Annual Interest Rate (%)"
            name="discountRate"
            type="text"
            isRequired={true}
            placeholder="Enter Interest Rate (e.g. 7.5)"
            rules={discountRateRules}
          />
        </Col>
        <Col xs={24} md={12}>
          <CustomInput
            label="Max Fund Required (%)"
            name="maxFundRequiredPct"
            type="text"
            isRequired={true}
            placeholder="Enter Max Fund Required % (60-80)"
            rules={maxFundRequiredPctRules}
          />
        </Col>
        <Col xs={24}>
          <CustomInput
            label="Comments (Max 500 chars)"
            name="comments"
            rows={4}
            isTextArea={true}
            type="text"
            isRequired={true}
            placeholder="Enter your Comments"
            rules={CommentsRules}
          />
        </Col>

        {/* Platform Fee */}
        <Col xs={24} md={12}>
          <CustomSelect
            label="Platform Fee (%)"
            name="platformFee"
            isRequired={true}
            placeholder="Select Platform Fee"
            options={platformFeeData?.map((fee) => ({
              label: convertToPercentage(fee.platformFee),
              value: fee.platformFeeId,
            }))}
            onChange={handlePlatformChange}
            rules={[{ required: true, message: "Please select platform fee" }]}
          />
          <div style={{ marginTop: 8 }}>
            {selectedPlatform ? (
              <Card
                style={{
                  background: "#f5faff",
                  borderRadius: "6px",
                  minHeight: "10%",
                }}
              >
                {[
                  {
                    label: "Platform ID",
                    value: selectedPlatform.platformFeeId,
                  },
                  {
                    label: "Platform Fees",
                    value: formatFee(selectedPlatform.platformFee, "percent"),
                  },
                  {
                    label: "Stamp Fees",
                    value: formatFee(selectedPlatform.stampFee, "amount"),
                  },
                  {
                    label: "Penal Charges",
                    value: formatFee(selectedPlatform.penalCharge, "percent"),
                  },
                  {
                    label: "Miscellaneous Fees",
                    value: formatFee(
                      selectedPlatform.miscellaneousFees,
                      "amount",
                    ),
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto 1fr",
                      marginBottom: "6px",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{item.label}</span>
                    <span style={{ textAlign: "center" }}>:</span>
                    <span style={{ textAlign: "right" }}>{item.value}</span>
                  </div>
                ))}
              </Card>
            ) : (
              <Text type="secondary">No Platform Fee details found</Text>
            )}
          </div>
        </Col>

        {BUS_ENT_DOC_CONFIG.map((doc) => (
          <Col xs={24} lg={12} key={doc.key} style={{ height: 120 }}>
            <BusEntDocUploadCard
              doc={doc}
              busEntId={data?.busEntId || data?.requestId}
              file={file}
              setFile={setFile}
            />
          </Col>
        ))}
      </Row>

      {/* Divider */}
      <Divider style={{ borderColor: "#CBD5E2" }} />

      <Spin spinning={busEntByIdLoading}>
        {checkboxList?.map((checkbox) => (
          <Row
            key={checkbox.checkboxId}
            align="middle"
            justify="space-between"
            style={{ marginBottom: "8px" }}
          >
            <Col flex="auto">
              <Form.Item
                name={checkbox.checkboxId}
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox>{checkbox.checkboxName}</Checkbox>
              </Form.Item>
            </Col>

            {!protectedCheckboxIds.includes(checkbox.checkboxId) && (
              <Col>
                <MinusCircleOutlined
                  style={{
                    color: "#ff4d4f",
                    fontSize: "18px",
                    cursor: deleteLoading ? "not-allowed" : "pointer",
                    opacity: deleteLoading ? 0.5 : 1,
                  }}
                  onClick={() =>
                    !deleteLoading && handleRemoveCheckbox(checkbox.checkboxId)
                  }
                />
              </Col>
            )}
          </Row>
        ))}
      </Spin>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Form.Item
            validateStatus={checkboxError ? "error" : ""}
            help={checkboxError}
          >
            <CustomInput
              label="Additional Checkbox"
              placeholder="Enter checkbox name"
              value={customCheckbox}
              onChange={(e) => {
                const value = e.target.value;
                setCustomCheckbox(value);

                const validationError = validateCheckboxName(value);

                if (value.length > 200) {
                  setCheckboxError(
                    "Checkbox name cannot exceed 200 characters",
                  );
                } else {
                  setCheckboxError(validationError);
                }
              }}
            />
          </Form.Item>

          <div
            style={{
              textAlign: "right",
              fontSize: "12px",
              color: "#8c8c8c",
              marginTop: "8px",
            }}
          >
            {customCheckbox.length}/200
          </div>
        </Col>

        <Col span={24}>
          <div
            onClick={!addSubmitLoading ? handleAddCheckbox : undefined}
            style={{
              border: "1px dashed #d9d9d9",
              borderRadius: "8px",
              padding: "12px",
              textAlign: "center",
              cursor: addSubmitLoading ? "not-allowed" : "pointer",
              fontSize: "16px",
              color: "#262626",
              opacity: addSubmitLoading ? 0.6 : 1,
            }}
          >
            {addSubmitLoading ? "Adding..." : "+ Add Checkbox"}
          </div>
        </Col>
      </Row>

      <FormFooter
        onCancelClick={() => {
          onClose();
          form.resetFields();
          lastDataKeyRef.current = null;
          prevCheckboxDetailsRef.current = null;
          setCheckboxList([]);
        }}
        submitLoading={approveSubmitLoading || requestApproveLoading}
        disabled={!file.fileUrl && !data?.requestId}
        btnName={BTN_TXT.SUBMIT}
        error={error}
      />
    </Form>

    <Modal
      title={CREDIT_LIMIT_MSGS.SUPER_ADMIN_ONLY_TITLE}
      open={superAdminRestrictionOpen}
      onCancel={() => setSuperAdminRestrictionOpen(false)}
      footer={[
        <Button key="close" type="primary" onClick={() => setSuperAdminRestrictionOpen(false)}>
          Close
        </Button>,
      ]}
    >
      <Text>{CREDIT_LIMIT_MSGS.SUPER_ADMIN_ONLY}</Text>
    </Modal>
    </>
  );
};

export default ApproveEntBus;
